import OwnerLayout from "../layout/OwnerLayout";
import { useState, useEffect } from "react";
import { MdSearch, MdLock } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import FraudCard from "../components/FraudCard";
import { API_BASE_URL } from "../../config/api";
import { authHeader, handleApiResponse } from "../../utils/authHeader";
import RecentFrauds from "../components/RecentFrauds";
import FRAUD_TYPES from "../../config/fraudTypes";

const OwnerDashboard = () => {
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [fraudType,setFraudType]= useState("all");
  const [notFound, setNotFound] = useState(false);
  const [subStatus, setSubStatus] = useState(null); // null = still loading
  const [searchAllowance, setSearchAllowance] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({});
  const [searchError, setSearchError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Always fetch fresh from API — never trust stale localStorage
    fetch(`${API_BASE_URL}/api/subscription/my`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const status = data?.subscription?.status || "trial";
        setSubStatus(status);
        setSearchAllowance(data?.searchAllowance || { limit: 0, used: 0, remaining: 0 });
        setPlanFeatures(data?.planFeatures || {});
        localStorage.setItem("subStatus", status);
      })
      .catch(() => {
        setSubStatus("trial");
        setSearchAllowance({ limit: 0, used: 0, remaining: 0 });
      });
  }, []);

  const isPro = subStatus === "active";
  const canSearch = isPro || (
    ["trial", "pending"].includes(subStatus) && searchAllowance && searchAllowance.limit !== 0
  );

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true); setNotFound(false); setSearchError("");
    try {
      const params = new URLSearchParams({ query: query.trim() });
      if (fraudType !== "all") params.append("fraudType", fraudType);
      const res  = await fetch(`${API_BASE_URL}/api/fraud/search?${params}`, { headers: authHeader() });
      if (!handleApiResponse(res)) return; // Handle 401 redirects
      const data = await res.json();
      if (!res.ok) {
        setSearchError(data.message || "Search failed. Please try again.");
        if (typeof data.searchesRemaining === "number") {
          setSearchAllowance(current => ({ ...current, remaining: data.searchesRemaining, used: data.searchesUsed }));
        }
        return;
      }

      setResults(data);
      setSearched(true);
      setNotFound(data.length === 0);
      setSearchAllowance(current => current && current.limit >= 0
        ? { ...current, used: current.used + 1, remaining: Math.max(0, current.remaining - 1) }
        : current);
    } catch {
      setSearchError("Could not complete the search. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery(""); setResults([]);
    setSearched(false); setNotFound(false);
    setFraudType("all");
  };

  return (
    <OwnerLayout>
      <div className="dashboard-header">
        <p className="welcome-text">Welcome!</p>
        <h2 className="company-name">Self-Drive Car Rental — Fraud Check</h2>
      </div>

      <div className="owner-dashboard-wrapper">


        {/* ── SEARCH — MAIN FOCUS ── */}
        {subStatus === null ? (
          /* Loading skeleton — prevents flicker between locked/unlocked states */
          <div style={{ background: "#fff", borderRadius: 20, padding: "28px 24px", marginBottom: 20, boxShadow: "0 4px 24px rgba(67,24,255,0.08)", border: "1.5px solid #ede9fe" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f4f7fe", margin: "0 auto 12px" }} />
              <div style={{ height: 20, width: 200, background: "#f4f7fe", borderRadius: 8, margin: "0 auto 8px" }} />
              <div style={{ height: 14, width: 280, background: "#f4f7fe", borderRadius: 8, margin: "0 auto" }} />
            </div>
            <div style={{ height: 50, borderRadius: 12, background: "#f4f7fe", marginBottom: 12 }} />
            <div style={{ height: 48, borderRadius: 12, background: "#f4f7fe" }} />
          </div>
        ) : canSearch ? (
          /* ── Search UI for plans with an allowance ── */
          <div style={{ background: "#fff", borderRadius: 20, padding: "28px 24px", marginBottom: 20, boxShadow: "0 4px 24px rgba(67,24,255,0.08)", border: "1.5px solid #ede9fe" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🔍</div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1b2559" }}>
                Check Customer Before Renting
              </h3>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7280" }}>
                Search by Phone Number, Name, Driving License or Aadhaar — instantly check fraud history
              </p>
              {searchAllowance?.limit >= 0 && (
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#0891b2", fontWeight: 700 }}>
                  {searchAllowance.remaining} of {searchAllowance.limit} searches remaining today
                </p>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 20, color: "#4318FF" }}>
                  <MdSearch />
                </span>
                <input
                  type="text"
                  placeholder="Enter Phone, Name, DL Number or Aadhaar…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  style={{
                    width: "100%", height: 50, padding: "0 16px 0 44px",
                    borderRadius: 12, border: "2px solid #4318FF30",
                    fontSize: 14, outline: "none",
                    background: "#f9faff", color: "#1b2559",
                    boxSizing: "border-box", transition: "border 0.2s",
                  }}
                  onFocus={e => e.target.style.border = "2px solid #4318FF"}
                  onBlur={e => e.target.style.border = "2px solid #4318FF30"}
                />
              </div>
              <select value={fraudType} onChange={e => setFraudType(e.target.value)}
                style={{ height: 50, padding: "0 14px", borderRadius: 12, border: "2px solid #4318FF30", fontSize: 13, background: "#f9faff", color: "#1b2559", cursor: "pointer", outline: "none" }}>
                <option value="all">All Fraud Types</option>
                {FRAUD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleSearch} disabled={loading || !query.trim() || searchAllowance?.remaining === 0}
                style={{
                  flex: 1, height: 48, background: !query.trim() || searchAllowance?.remaining === 0 ? "#e0e5f2" : "linear-gradient(135deg, #4318FF, #7551FF)",
                  color: !query.trim() || searchAllowance?.remaining === 0 ? "#a3aed0" : "#fff",
                  border: "none", borderRadius: 12, fontWeight: 800,
                  fontSize: 15, cursor: !query.trim() || searchAllowance?.remaining === 0 ? "not-allowed" : "pointer",
                  boxShadow: query.trim() ? "0 4px 16px rgba(67,24,255,0.3)" : "none",
                  transition: "all 0.2s",
                }}>
                {loading ? "🔍 Searching..." : "🔍 Search Now"}
              </button>
              {searched && (
                <button onClick={handleClear}
                  style={{ height: 48, padding: "0 20px", background: "#fff1f1", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  ✕ Clear
                </button>
              )}
            </div>

            {searchError && <p role="alert" style={{ color: "#dc2626", fontSize: 13, fontWeight: 600, margin: "10px 0 0" }}>{searchError}</p>}

            {!searched && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14, justifyContent: "center" }}>
                {["📞 Phone Number", "👤 Full Name", "🪪 Driving License", "🔢 Aadhaar"].map(tip => (
                  <span key={tip} style={{ fontSize: 11, background: "#f4f7fe", color: "#6b7280", padding: "4px 10px", borderRadius: 20, fontWeight: 600 }}>
                    {tip}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ── TRIAL/PENDING: Locked search card ── */
          <div style={{
            background: "#fff", borderRadius: 20, padding: "28px 24px", marginBottom: 20,
            boxShadow: "0 4px 24px rgba(67,24,255,0.08)", border: "1.5px solid #ede9fe",
            display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
          }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>🔒</div>
            <h3 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: "#1b2559" }}>
              Search is not included in this plan
            </h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#6b7280", maxWidth: 360, lineHeight: 1.6 }}>
              Upgrade to instantly verify any customer by phone, name, driving license or Aadhaar before you hand over the keys.
            </p>

            {/* Feature list — 2 column grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 22, width: "100%", maxWidth: 400 }}>
              {[
                ["🔍", "Higher search allowance"],
                ["⚡", "Phone, Name, DL, Aadhaar"],
                ["📧", "Real-time email alerts"],
                ["📊", "Dashboard analytics"],
                ["📄", "CSV export"],
                ["🛡️", "Protect your business"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f4f7fe", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#1b2559", fontWeight: 600 }}>
                  <span style={{ fontSize: 15 }}>{icon}</span> {text}
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate("/owner/subscription")}
              style={{
                padding: "13px 32px", background: "linear-gradient(135deg, #4318FF, #7551FF)",
                color: "#fff", border: "none", borderRadius: 12,
                fontWeight: 800, fontSize: 15, cursor: "pointer",
                boxShadow: "0 4px 16px rgba(67,24,255,0.35)",
                width: "100%", maxWidth: 360,
              }}>
              Upgrade to Pro — Unlock Search
            </button>
            <p style={{ marginTop: 10, fontSize: 11, color: "#a3aed0" }}>
              You can still report frauds for free — forever.
            </p>
          </div>
        )}

        {/* ── SEARCH RESULTS ── */}
        {searched && (
          <div style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 20, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>

            {notFound ? (
              /* No fraud found — GREEN good signal */
              <div style={{ textAlign: "center", padding: "28px 16px", background: "#f0fdf4", borderRadius: 14, border: "1.5px solid #bbf7d0" }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
                <p style={{ fontWeight: 800, color: "#15803d", fontSize: 16, margin: 0 }}>
                  No Fraud Records Found
                </p>
                <p style={{ color: "#16a34a", fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
                  "<b>{query}</b>" has no approved fraud reports on record.<br/>
                  This customer appears to be safe to rent to.
                </p>
              </div>
            ) : (
              /* Fraud found — RED warning */
              <>
                <div style={{ background: "#fee2e2", borderRadius: 12, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 24 }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: 800, color: "#991b1b", fontSize: 14 }}>
                      {results.length} Fraud Record{results.length > 1 ? "s" : ""} Found!
                    </div>
                    <div style={{ fontSize: 12, color: "#b91c1c" }}>
                      This customer has fraud reports. Be careful before renting.
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {results.map(fraud => (
                    <FraudCard key={fraud._id}
                      fullName={fraud.fullName}
                      fraudType={fraud.fraudType}
                      dlNo={fraud.drivingLicense}
                      location={`${fraud.fraudCity || ""}${fraud.fraudArea ? " - " + fraud.fraudArea : ""}`}
                      phone={fraud.phoneNumbers?.join(", ")}
                      date={fraud.createdAt}
                      status={fraud.status}
                      fraud={fraud}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── RECENT FRAUD ALERTS — only when not searching ── */}
        {!searched && planFeatures.canReceiveAlerts !== false && (
          <RecentFrauds canViewReportImages={planFeatures.canViewReportImages !== false} />
        )}

      </div>
    </OwnerLayout>
  );
};

export default OwnerDashboard;